import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Typography,
  Grid,
  Card,
  CardContent,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  CircularProgress,
  Alert,
  Snackbar,
  useTheme,
  useMediaQuery
} from '@mui/material';
import {
  Storefront,
  LocalOffer,
  Group,
  AddCircleOutline,
  Logout,
  CheckCircle,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { url } from '../../constants';

const AdminDashboard = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const navigate = useNavigate();

  // State
  const [stats, setStats] = useState(null);
  const [vendors, setVendors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('admin_token');
      if (!token) {
        navigate('/admin/login');
        return;
      }

      // Fetch Stats
      const statsRes = await fetch(`${url}/admin/stats`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (!statsRes.ok) throw new Error('Failed to fetch stats');
      const statsData = await statsRes.json();
      setStats(statsData);

      // Fetch Vendors
      const vendorsRes = await fetch(`${url}/admin/vendors`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (!vendorsRes.ok) throw new Error('Failed to fetch vendors');
      const vendorsData = await vendorsRes.json();
      setVendors(vendorsData);

    } catch (err) {
      console.error(err);
      setError(err.message);
      if (err.message.includes('token')) {
        navigate('/admin/login');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('admin_token');
    localStorage.removeItem('admin_data');
    navigate('/admin/login');
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', height: '100vh', alignItems: 'center', justifyContent: 'center' }}>
        <CircularProgress sx={{ color: '#1a7f3a' }} />
      </Box>
    );
  }

  return (
    <Box sx={{ bgcolor: '#F8FAFC', minHeight: '100vh', py: isMobile ? 2 : 4 }}>
      <Container maxWidth="xl">
        
        {/* Header */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4 }}>
          <Typography variant={isMobile ? 'h5' : 'h4'} fontWeight={800} color="#1E293B">
            Admin Dashboard
          </Typography>
          <Button 
            variant="outlined" 
            color="error" 
            startIcon={<Logout />}
            onClick={handleLogout}
          >
            Logout
          </Button>
        </Box>

        {error && <Alert severity="error" sx={{ mb: 3 }}>{error}</Alert>}

        {/* Stats Grid */}
        {stats && (
          <Grid container spacing={3} sx={{ mb: 5 }}>
            <Grid item xs={12} sm={6} md={3}>
              <Card sx={{ borderRadius: 2, boxShadow: '0 4px 6px rgba(0,0,0,0.05)' }}>
                <CardContent sx={{ display: 'flex', alignItems: 'center', p: 3 }}>
                  <Box sx={{ bgcolor: '#E0F2FE', p: 1.5, borderRadius: 2, mr: 2 }}>
                    <Storefront sx={{ color: '#0284C7', fontSize: 32 }} />
                  </Box>
                  <Box>
                    <Typography variant="body2" color="text.secondary" fontWeight={600}>Total Vendors</Typography>
                    <Typography variant="h4" fontWeight={800} color="#1E293B">{stats.total_vendors}</Typography>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
            
            <Grid item xs={12} sm={6} md={3}>
              <Card sx={{ borderRadius: 2, boxShadow: '0 4px 6px rgba(0,0,0,0.05)' }}>
                <CardContent sx={{ display: 'flex', alignItems: 'center', p: 3 }}>
                  <Box sx={{ bgcolor: '#DCFCE7', p: 1.5, borderRadius: 2, mr: 2 }}>
                    <CheckCircle sx={{ color: '#16A34A', fontSize: 32 }} />
                  </Box>
                  <Box>
                    <Typography variant="body2" color="text.secondary" fontWeight={600}>Active Vendors</Typography>
                    <Typography variant="h4" fontWeight={800} color="#1E293B">{stats.active_vendors}</Typography>
                  </Box>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <Card sx={{ borderRadius: 2, boxShadow: '0 4px 6px rgba(0,0,0,0.05)' }}>
                <CardContent sx={{ display: 'flex', alignItems: 'center', p: 3 }}>
                  <Box sx={{ bgcolor: '#FEF3C7', p: 1.5, borderRadius: 2, mr: 2 }}>
                    <LocalOffer sx={{ color: '#D97706', fontSize: 32 }} />
                  </Box>
                  <Box>
                    <Typography variant="body2" color="text.secondary" fontWeight={600}>Active Deals</Typography>
                    <Typography variant="h4" fontWeight={800} color="#1E293B">{stats.active_deals}</Typography>
                  </Box>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <Card sx={{ borderRadius: 2, boxShadow: '0 4px 6px rgba(0,0,0,0.05)' }}>
                <CardContent sx={{ display: 'flex', alignItems: 'center', p: 3 }}>
                  <Box sx={{ bgcolor: '#F3E8FF', p: 1.5, borderRadius: 2, mr: 2 }}>
                    <Group sx={{ color: '#9333EA', fontSize: 32 }} />
                  </Box>
                  <Box>
                    <Typography variant="body2" color="text.secondary" fontWeight={600}>Total Users</Typography>
                    <Typography variant="h4" fontWeight={800} color="#1E293B">{stats.total_users}</Typography>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        )}

        {/* Vendors Section Header */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Typography variant="h5" fontWeight={700} color="#1E293B">
            Vendor Management
          </Typography>
          <Button 
            variant="contained" 
            startIcon={<AddCircleOutline />}
            onClick={() => navigate('/admin/vendors/add')} // ✅ FIXED: Now navigates to full page
            sx={{ bgcolor: '#1a7f3a', '&:hover': { bgcolor: '#0f5328' } }}
          >
            Add Vendor
          </Button>
        </Box>

        {/* Vendors Table */}
        <TableContainer component={Paper} sx={{ borderRadius: 2, boxShadow: '0 4px 6px rgba(0,0,0,0.05)' }}>
          <Table>
            <TableHead sx={{ bgcolor: '#F1F5F9' }}>
              <TableRow>
                <TableCell sx={{ fontWeight: 700 }}>ID</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Business Name</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Email</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Phone</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Deals</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Status</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {vendors.map((vendor) => (
                <TableRow key={vendor._id} hover>
                  <TableCell>#{vendor.vendor_id}</TableCell>
                  <TableCell fontWeight={600}>{vendor.business_name}</TableCell>
                  <TableCell>{vendor.email}</TableCell>
                  <TableCell>{vendor.phone || 'N/A'}</TableCell>
                  <TableCell>{vendor.total_deals}</TableCell>
                  <TableCell>
                    <Chip 
                      label={vendor.is_active ? 'Active' : 'Inactive'} 
                      color={vendor.is_active ? 'success' : 'error'}
                      size="small"
                    />
                  </TableCell>
                </TableRow>
              ))}
              {vendors.length === 0 && (
                <TableRow>
                  <TableCell colSpan={6} align="center" sx={{ py: 3 }}>
                    No vendors found.
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>

        {/* Global Snackbar */}
        <Snackbar open={snackbar.open} autoHideDuration={4000} onClose={() => setSnackbar({ ...snackbar, open: false })} anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
          <Alert onClose={() => setSnackbar({ ...snackbar, open: false })} severity={snackbar.severity} sx={{ width: '100%', fontWeight: 600 }}>
            {snackbar.message}
          </Alert>
        </Snackbar>

      </Container>
    </Box>
  );
};

export default AdminDashboard;